
import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface OnboardingStep {
  id: string;
  title: string;
  completed: boolean;
}

interface OnboardingContextType {
  currentStep: number;
  steps: OnboardingStep[];
  isOnboardingOpen: boolean;
  nextStep: () => void;
  prevStep: () => void;
  completeStep: (stepId: string) => void;
  closeOnboarding: () => void;
  openOnboarding: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [steps, setSteps] = useState<OnboardingStep[]>([
    { id: 'profile', title: 'Completar Perfil', completed: false },
    { id: 'cashback', title: 'Configurar Cashback', completed: false },
    { id: 'terms', title: 'Aceitar Termos', completed: false },
    { id: 'welcome', title: 'Bem-vindo', completed: false },
  ]);

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeStep = (stepId: string) => {
    setSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, completed: true } : step
    ));
  };

  const closeOnboarding = () => {
    setIsOnboardingOpen(false);
  };

  const openOnboarding = () => {
    setIsOnboardingOpen(true);
  };

  return (
    <OnboardingContext.Provider value={{
      currentStep,
      steps,
      isOnboardingOpen,
      nextStep,
      prevStep,
      completeStep,
      closeOnboarding,
      openOnboarding,
    }}>
      {children}
    </OnboardingContext.Provider>
  );
};
